import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion, useReducedMotion } from "framer-motion";
import { useT, LanguageSelector } from "@/i18n";
import { HeroUIProvider, Card, CardBody, Input, Button, Checkbox, Chip, Divider, Tooltip } from "@heroui/react";
import { Mail, Lock, Eye, EyeOff, ShieldCheck, Globe2, Boxes, ArrowRight, Building2 } from "lucide-react";
import { SupplyNetwork } from "@/components/login/SupplyNetwork";
import "@/components/login/login.css";

const LOGO = "/heliopolis-logo.png";

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem("remember_email") || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem("remember_email"));
  const reduce = useReducedMotion();

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const err = localStorage.getItem("auth_error");
    if (err) {
      toast.error(err);
      localStorage.removeItem("auth_error");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      if (remember) localStorage.setItem("remember_email", email); else localStorage.removeItem("remember_email");
      toast.success(t("login.welcome"));
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("login.failed"));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const forgot = () => toast.info("Please contact ops@heliopolispharmaceuticals.com to reset your password.");
  const fade = (d = 0) => (reduce ? {} : { initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] } });

  const features = [
    { icon: Globe2, label: "Global Supply Network" },
    { icon: ShieldCheck, label: "Secure B2B Access" },
    { icon: Boxes, label: "Pharmaceutical Logistics" },
  ];

  return (
    <HeroUIProvider>
      <div className="hl-root relative min-h-[100svh] overflow-hidden text-[#0D1B2A] font-[Inter,ui-sans-serif,system-ui] light" data-testid="login-page">
        <div className="hl-bg" aria-hidden="true" />
        <div className="hl-glow hl-glow-a" aria-hidden="true" />
        <div className="hl-glow hl-glow-b" aria-hidden="true" />
        <div className="hl-molecules" aria-hidden="true" />

        <div className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-[1440px] flex-col lg:flex-row">
          {/* BRAND / VISUAL */}
          <section className="relative flex flex-col px-6 pt-[max(1.25rem,env(safe-area-inset-top))] lg:w-[58%] lg:px-14 lg:py-12" data-testid="login-brand-panel">
            <motion.div {...fade(0)} className="flex items-center justify-between lg:justify-start">
              <img src={LOGO} alt="Heliopolis Pharmaceuticals" className="h-16 sm:h-20 lg:h-24 w-auto mx-auto lg:mx-0" data-testid="login-logo" />
              <div className="absolute right-6 top-[max(1.25rem,env(safe-area-inset-top))] lg:hidden"><LanguageSelector /></div>
            </motion.div>

            <div className="mt-6 text-center lg:mt-14 lg:max-w-xl lg:text-left">
              <motion.p {...fade(0.05)} className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#245FAF]" data-testid="login-portal-label">
                {t("login.portal")}
              </motion.p>
              <motion.h1 {...fade(0.1)} className="mt-3 hidden text-4xl font-extrabold leading-[1.05] tracking-tight lg:block xl:text-[3.4rem]" data-testid="login-headline">
                Global pharmaceutical supply.
                <span className="block bg-gradient-to-r from-[#377DD3] to-[#245FAF] bg-clip-text text-transparent">Connected.</span>
              </motion.h1>
              <motion.h1 {...fade(0.1)} className="mt-2 text-3xl font-extrabold tracking-tight lg:hidden" data-testid="login-headline-mobile">Welcome back</motion.h1>
              <motion.p {...fade(0.15)} className="mt-4 text-sm leading-relaxed text-[#64748B] sm:text-base lg:mt-5 lg:max-w-lg" data-testid="login-supporting">
                Secure access to orders, shipments, procurement and invoices through the Heliopolis Pharmaceuticals supply network.
              </motion.p>
              <motion.div {...fade(0.2)} className="mt-6 hidden flex-wrap gap-2 lg:flex" data-testid="login-feature-chips">
                {features.map((f) => (
                  <Chip key={f.label} variant="flat" radius="full" className="border border-[#377DD3]/15 bg-white/70 text-[#0D1B2A] backdrop-blur" startContent={<f.icon size={14} className="text-[#377DD3]" />}>
                    <span className="text-xs font-medium">{f.label}</span>
                  </Chip>
                ))}
              </motion.div>
            </div>

            <div className="relative mt-4 hidden flex-1 lg:block" aria-hidden="true">
              <SupplyNetwork reduce={reduce} />
            </div>

            <footer className="mt-auto hidden pt-8 text-xs text-[#64748B] lg:block" data-testid="login-company-footer">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1.5 font-semibold text-[#0D1B2A]"><Building2 size={13} className="text-[#377DD3]" /> Heliopolis Pharmaceuticals</span>
                <span>Dubai DMCC Free Zone</span>
                <span className="hidden sm:inline">·</span>
                <span>B2B Pharmaceutical Supply • Named Patient Supply</span>
                <span className="hidden sm:inline">·</span>
                <a href="mailto:ops@heliopolispharmaceuticals.com" className="font-medium text-[#245FAF] hover:underline" data-testid="login-contact-email">ops@heliopolispharmaceuticals.com</a>
              </div>
            </footer>
          </section>

          {/* LOGIN PANEL */}
          <section className="flex flex-1 flex-col items-center justify-center px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6 lg:px-12 lg:py-12" data-testid="login-panel">
            <motion.div {...fade(0.2)} className="w-full max-w-[460px]">
              <div className="mb-4 hidden justify-end lg:flex"><LanguageSelector /></div>
              <Card shadow="none" className="hl-card rounded-[26px] border border-[#377DD3]/15 bg-white/80 backdrop-blur-xl" data-testid="login-card">
                <CardBody className="p-6 sm:p-8">
                  <div className="hidden lg:block">
                    <h2 className="text-2xl font-bold tracking-tight" data-testid="login-welcome">Welcome back</h2>
                    <p className="mt-1 text-sm text-[#64748B]">Sign in to Heliopolis Supply Chain Portal</p>
                  </div>
                  <form onSubmit={submit} className="mt-2 space-y-4 lg:mt-7" data-testid="login-form">
                    <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wide text-[#0D1B2A]" htmlFor="hl-email">{t("login.email")}</label>
                    <Input
                      id="hl-email"
                      type="email"
                      isRequired
                      aria-label={t("login.email")}
                      placeholder="you@company.com"
                      value={email}
                      onValueChange={setEmail}
                      variant="bordered"
                      radius="lg"
                      size="lg"
                      startContent={<Mail size={18} className="text-[#64748B]" />}
                      classNames={{ input: "text-base", inputWrapper: "hl-input h-12 bg-white/70 border-[#D7E3F2] data-[hover=true]:border-[#377DD3]/60 group-data-[focus=true]:border-[#377DD3]" }}
                      data-testid="login-email"
                    />
                    </div>
                    <div className="space-y-1.5 !mt-4">
                    <label className="text-xs font-semibold uppercase tracking-wide text-[#0D1B2A]" htmlFor="hl-password">{t("login.password")}</label>
                    <Input
                      id="hl-password"
                      type={showPw ? "text" : "password"}
                      isRequired
                      aria-label={t("login.password")}
                      placeholder="••••••••"
                      value={password}
                      onValueChange={setPassword}
                      variant="bordered"
                      radius="lg"
                      size="lg"
                      startContent={<Lock size={18} className="text-[#64748B]" />}
                      endContent={
                        <Tooltip content={showPw ? "Hide password" : "Show password"} placement="top" size="sm">
                          <button type="button" onClick={() => setShowPw((v) => !v)} className="text-[#64748B] hover:text-[#377DD3] focus:outline-none" aria-label="Toggle password visibility" data-testid="login-toggle-password">
                            {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </Tooltip>
                      }
                      classNames={{ input: "text-base", inputWrapper: "hl-input h-12 bg-white/70 border-[#D7E3F2] data-[hover=true]:border-[#377DD3]/60 group-data-[focus=true]:border-[#377DD3]" }}
                      data-testid="login-password"
                    />
                    </div>
                    <div className="flex items-center justify-between !mt-4">
                      <Checkbox isSelected={remember} onValueChange={setRemember} size="sm" radius="sm" color="primary" classNames={{ label: "text-sm text-[#64748B]" }} data-testid="login-remember">
                        Remember me
                      </Checkbox>
                      <button type="button" onClick={forgot} className="text-sm font-medium text-[#245FAF] hover:underline" data-testid="login-forgot">Forgot password?</button>
                    </div>
                    <Button
                      type="submit"
                      isLoading={busy}
                      radius="lg"
                      size="lg"
                      className="hl-btn !mt-4 h-12 w-full bg-gradient-to-r from-[#377DD3] to-[#245FAF] text-base font-semibold text-white shadow-[0_10px_24px_-10px_rgba(55,125,211,0.7)]"
                      endContent={!busy && <ArrowRight size={18} />}
                      data-testid="login-submit"
                    >
                      {busy ? t("login.signingIn") : "Sign in securely"}
                    </Button>
                    <p className="flex items-center justify-center gap-1.5 text-xs text-[#64748B] !mt-3" data-testid="login-protected-note">
                      <ShieldCheck size={13} className="text-[#F5C64D]" /> Protected access • Heliopolis Pharmaceuticals
                    </p>
                  </form>

                  <div className="my-5 flex items-center gap-3">
                    <Divider className="flex-1 bg-[#D7E3F2]" />
                    <span className="text-[11px] uppercase tracking-wider text-[#64748B]">{t("login.or")}</span>
                    <Divider className="flex-1 bg-[#D7E3F2]" />
                  </div>

                  <Button variant="bordered" radius="lg" size="lg" className="hl-btn h-12 w-full border-[#D7E3F2] bg-white/60 font-medium text-[#0D1B2A]" onClick={googleLogin} data-testid="google-login"
                    startContent={<svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.4 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.2 0 11.6-2 15.4-5.6l-7.5-5.8c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.7-3.9-13.6-9.4l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/></svg>}>
                    {t("login.google")}
                  </Button>
                  <p className="mt-4 text-xs leading-relaxed text-[#64748B]" data-testid="login-team-note">{t("login.teamNote")}</p>
                </CardBody>
              </Card>

              <div className="mt-6 text-center text-xs text-[#64748B] lg:hidden" data-testid="login-company-footer-mobile">
                <p className="font-semibold text-[#0D1B2A]">Heliopolis Pharmaceuticals · Dubai DMCC Free Zone</p>
                <p className="mt-0.5">B2B Pharmaceutical Supply • Named Patient Supply</p>
                <a href="mailto:ops@heliopolispharmaceuticals.com" className="mt-0.5 inline-block font-medium text-[#245FAF]">ops@heliopolispharmaceuticals.com</a>
              </div>
            </motion.div>
          </section>
        </div>
      </div>
    </HeroUIProvider>
  );
}
